import { translate } from "@/i18n/i18n.config";
import { flash, hideFlash } from "@/utils/flash";
import * as Sentry from "@sentry/vue";
import type { RouteLocationNormalized, Router } from "vue-router";

// After a deploy the previous build's chunks are gone, so a tab opened before
// it fails as soon as it lazily loads a route it has not visited yet. Vite
// reports that as `vite:preloadError`
// (https://vite.dev/guide/build#load-error-handling). Only a full page load
// recovers: the browser remembers a failed module URL for the life of the
// document.
//
// That load has to go where the user was *going*. vue-router loads lazy route
// components before it commits a navigation, so `location.href` is still the
// page being left (#13673).

const STORAGE_KEY = "chunkReloadAttempts";
// How long load n of a target waits. One immediate load fixes a tab opened
// before a deploy. A file still missing on the freshly loaded page means the
// deploy is still rolling out: the CDN got it from a pod on the previous build
// (#13888, #13928). Loads in quick succession all land in that rollout, so
// the later ones are spread over a few minutes to outlast it.
const RELOAD_DELAYS_MS = [0, 10_000, 30_000, 60_000, 120_000];
// Longer than the whole schedule plus slow boots of the reloaded pages, so a
// chunk that stays missing cannot turn into a reload loop.
const ATTEMPT_WINDOW_MS = 10 * 60 * 1000;
// One click fires several events (one per failing lazy route record and CSS
// file), all served by the same reload. Short, so links work again if the user
// cancels the unload in a `beforeunload` prompt.
const RELOAD_LATCH_MS = 5000;

const WAITING_TOAST_GROUP = "page-load-retrying";

// When each load of a target started, so each one expires on its own.
type Attempts = Record<string, number[]>;

interface Decision {
  to: RouteLocationNormalized | undefined;
  at: number;
}

interface ScheduledLoad extends Decision {
  target: string;
  // Set until the load starts.
  timer?: ReturnType<typeof setTimeout>;
}

// Vite's own message, the same in every browser.
const isCssError = (event: VitePreloadErrorEvent): boolean =>
  event.payload?.message?.startsWith("Unable to preload CSS") ?? false;

// The attempts of the last ATTEMPT_WINDOW_MS. Throws without storage.
function readAttempts(now: number): Attempts {
  const stored = JSON.parse(
    sessionStorage.getItem(STORAGE_KEY) ?? "{}"
  ) as Partial<Attempts>;
  const attempts: Attempts = {};
  for (const [target, times] of Object.entries(stored)) {
    const recent = (Array.isArray(times) ? times : []).filter(
      (at) => now - at < ATTEMPT_WINDOW_MS
    );
    if (recent.length) attempts[target] = recent;
  }
  return attempts;
}

// Counts full page loads per target across loads of this tab, so a chunk that
// is missing for good stops after the last of RELOAD_DELAYS_MS. `loads` is how
// many came before.
function claimReload(
  target: string,
  now: number
): { claimed: boolean; loads: number } {
  try {
    const attempts = readAttempts(now);
    const times = attempts[target] ?? [];
    if (times.length >= RELOAD_DELAYS_MS.length)
      return { claimed: false, loads: times.length };

    attempts[target] = [...times, now];
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(attempts));
    return { claimed: true, loads: times.length };
  } catch {
    // Without storage the loads cannot be counted, and an uncounted reload
    // could loop.
    return { claimed: false, loads: 0 };
  }
}

// Takes back the attempt claimed at `at` for a load that never happened.
function refundReload(target: string, at: number): void {
  try {
    const attempts = readAttempts(Date.now());
    const times = attempts[target] ?? [];
    const index = times.indexOf(at);
    if (index === -1) return;
    times.splice(index, 1);
    if (!times.length) delete attempts[target];
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(attempts));
  } catch {
    // Without storage nothing was counted.
  }
}

function hardLoad(target: string): void {
  const url = new URL(target);
  const current = new URL(location.href);
  // `assign` to the current URL (or one that differs only by its #hash)
  // scrolls within the document instead of loading it.
  if (
    url.origin === current.origin &&
    url.pathname === current.pathname &&
    url.search === current.search
  ) {
    location.reload();
  } else {
    location.assign(target);
  }
}

export function installPreloadErrorHandler(router: Router): () => void {
  // vue-router keeps its pending location private, and `beforeResolve` runs
  // after the lazy components are loaded, so track it here. Install this
  // before any other guard, so it holds the final target after redirects.
  let pending: RouteLocationNormalized | undefined;
  // What this page last decided for a failed navigation: load `target`, or
  // give up. One click fails several files (layout, page, stylesheets), and
  // the page's chunk can fail after the layout already ended the navigation,
  // when nothing is pending any more; those events follow the decision. A
  // newer navigation is a new click and gets its own, even within the latch
  // (e.g. after the user stayed in a beforeunload prompt). A load that is
  // still waiting holds for as long as it waits.
  let scheduled: ScheduledLoad | undefined;
  let gaveUp: Decision | undefined;
  const belongsTo = (
    decision: ScheduledLoad | Decision | undefined,
    now: number
  ): boolean =>
    decision !== undefined &&
    (now - decision.at < RELOAD_LATCH_MS ||
      ("timer" in decision && decision.timer !== undefined)) &&
    (pending === undefined || pending === decision.to);

  // Calls off the load this page scheduled, so it doesn't count: one still
  // waiting is stopped, and one that started but left the user here (e.g. in a
  // beforeunload prompt) never happened. A load that did happen started a new
  // page, so the limit still holds.
  const cancelScheduled = (): void => {
    if (!scheduled) return;
    clearTimeout(scheduled.timer);
    hideFlash(WAITING_TOAST_GROUP);
    refundReload(scheduled.target, scheduled.at);
    scheduled = undefined;
  };

  // A wait of up to two minutes would look like the click did nothing.
  const announceWait = (): void =>
    flash("info", translate("errors.pageLoadRetrying.title"), {
      description: translate("errors.pageLoadRetrying.description"),
      autohide: false,
      group: WAITING_TOAST_GROUP,
    });

  // Clear only for the navigation being tracked: a superseded navigation still
  // reaches afterEach (cancelled) or onError while the newer one is loading.
  const removeBeforeEach = router.beforeEach((to) => {
    pending = to;
  });
  const removeAfterEach = router.afterEach((to, _from, failure) => {
    if (to === pending) pending = undefined;
    // The user opened another page, which a waiting load would take them away
    // from.
    if (!failure && scheduled?.timer !== undefined) cancelScheduled();
  });
  const removeOnError = router.onError((_error, to) => {
    if (to === pending) pending = undefined;
  });

  // The file is still missing after fresh page loads, so this is no stale tab
  // any more. Say so instead of dropping the click.
  const giveUp = (
    event: VitePreloadErrorEvent,
    target: string,
    now: number,
    loads: number
  ): void => {
    // No load is coming, so let what did arrive be used: a page without some
    // of its styles beats one that does not open. A missing chunk still fails
    // its navigation, just without orphaned rejections (see below).
    event.preventDefault();
    gaveUp = { to: pending, at: now };

    flash("alert", translate("errors.pageLoadFailed.title"), {
      description: translate("errors.pageLoadFailed.description"),
      autohide: false,
      group: "page-load-failed",
    });
    // Not a message `beforeSend` drops as stale-chunk noise (sentry.ts).
    Sentry.captureMessage("Lazy chunk still missing after reload", {
      level: "warning",
      fingerprint: ["lazy-chunk-still-missing-after-reload"],
      tags: { route: String(pending?.name ?? "") },
      extra: {
        path: new URL(target).pathname,
        error: event.payload?.message,
        loads,
      },
    });
  };

  // The page is about to be replaced. A prevented chunk error resolves the
  // import to `undefined`, which fails the navigation all the same and leaves
  // no unhandled rejection from the imports vue-router stops awaiting. A
  // prevented CSS error, though, lets the chunk load and the route commit
  // under the pending load.
  const failUntilLoad = (event: VitePreloadErrorEvent): void => {
    if (!isCssError(event)) event.preventDefault();
  };

  const onPreloadError = (event: VitePreloadErrorEvent): void => {
    const now = Date.now();
    if (belongsTo(scheduled, now)) return failUntilLoad(event);
    if (belongsTo(gaveUp, now)) return event.preventDefault();

    // Without a pending navigation the import came from the page itself
    // (e.g. a lazily loaded dialog body), so reload that page.
    const target = new URL(
      pending ? router.resolve(pending).href : location.href,
      location.href
    ).href;

    // The link the page is waiting to load was clicked again: keep the wait
    // rather than start it over, and let the rest of this click follow it.
    if (scheduled?.timer !== undefined && scheduled.target === target) {
      scheduled.to = pending;
      announceWait();
      return failUntilLoad(event);
    }
    // Another link, or this page outlived its load.
    cancelScheduled();

    const { claimed, loads } = claimReload(target, now);
    if (!claimed) return giveUp(event, target, now, loads);

    const delay = RELOAD_DELAYS_MS[loads] ?? 0;
    if (delay > 0) announceWait();
    const load: ScheduledLoad = { to: pending, at: now, target };
    // A macrotask even without a delay, because on back/forward vue-router
    // rewinds the URL with `history.go()` after the error, which cancels a
    // load started earlier.
    load.timer = setTimeout(() => {
      load.timer = undefined;
      hardLoad(target);
    }, delay);
    scheduled = load;
    failUntilLoad(event);
  };

  window.addEventListener("vite:preloadError", onPreloadError);

  return () => {
    clearTimeout(scheduled?.timer);
    window.removeEventListener("vite:preloadError", onPreloadError);
    removeBeforeEach();
    removeAfterEach();
    removeOnError();
  };
}
